import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Save, Sparkles } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => {
  const t = localStorage.getItem('savenes_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function AssetNewPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const upcParam = searchParams.get('upc') || '';
  const [categories, setCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [form, setForm] = useState({
    name: '', category_id: '', manufacturer: '', model: '', serial_number: '',
    sku: '', upc: upcParam,
    description: '', purchase_date: '', purchase_cost: '', vendor: '',
    purchase_order: '', warranty_expires_at: '', warranty_terms: '',
    status: 'in_stock',
  });
  useEffect(() => {
    axios.get(`${API_URL}/api/assets/categories`, { headers: authHeader() }).then(r => setCategories(r.data.categories || []));
  }, []);

  // Auto-enrich from UPC param on first mount
  useEffect(() => {
    if (!upcParam) return;
    setEnriching(true);
    axios.post(`${API_URL}/api/assets/enrich-from-barcode`, { code: upcParam }, { headers: authHeader() })
      .then(r => {
        const p = r.data.product || {};
        if (p.source === 'unknown') {
          toast.message(`No product info found for ${upcParam}`, { description: 'Please fill in details manually.' });
          return;
        }
        toast.success(`Auto-filled from ${p.manufacturer || 'web'}`);
        setForm(f => ({
          ...f,
          name: p.name || f.name,
          manufacturer: p.manufacturer || f.manufacturer,
          model: p.model || f.model,
          sku: p.sku || f.sku,
          description: p.description || f.description,
          warranty_terms: p.warranty_terms || f.warranty_terms,
        }));
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Web lookup failed'))
      .finally(() => setEnriching(false));
  }, [upcParam]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.category_id) { toast.error('Name and category required'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.purchase_cost === '') payload.purchase_cost = null;
      else if (payload.purchase_cost) payload.purchase_cost = parseFloat(payload.purchase_cost);
      // Remove empty optionals
      Object.keys(payload).forEach(k => { if (payload[k] === '') delete payload[k]; });
      const r = await axios.post(`${API_URL}/api/assets`, payload, { headers: authHeader() });
      toast.success(`Asset ${r.data.asset_tag} created`);
      navigate(`/assets/${r.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const subs = categories.filter(c => c.parent_id);

  return (
    <div className="p-6 max-w-2xl mx-auto" data-testid="asset-new-page">
      <Link to="/assets/list" className="text-sm text-[#0E7490] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to assets
      </Link>
      <h1 className="text-2xl font-bold text-[#0A2342] mb-4">New asset</h1>
      {enriching && (
        <div className="mb-3 px-3 py-2 rounded-md bg-[#F0F9FF] border border-[#BAE6FD] text-xs text-[#075985] flex items-center gap-2" data-testid="enrich-banner">
          <Sparkles className="w-4 h-4 animate-pulse" /> Looking up <span className="font-mono">{upcParam}</span> online…
        </div>
      )}
      <form onSubmit={submit}>
        <Card className="p-5 border-[#E2E8F0] space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div className="md:col-span-2">
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => set('name', e.target.value)} required data-testid="new-name" />
            </div>
            <div>
              <Label>Category *</Label>
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)} required
                      className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white" data-testid="new-category">
                <option value="">Select…</option>
                {subs.map(s => {
                  const parent = categories.find(c => c.id === s.parent_id);
                  return <option key={s.id} value={s.id}>{parent ? parent.name + ' / ' : ''}{s.name}</option>;
                })}
              </select>
            </div>
            <div>
              <Label>Status</Label>
              <select value={form.status} onChange={e => set('status', e.target.value)}
                      className="w-full mt-1 rounded-md border border-[#E2E8F0] px-2 py-1.5 bg-white">
                <option value="in_stock">In Stock</option>
                <option value="assigned">Assigned</option>
                <option value="maintenance">Maintenance</option>
                <option value="retired">Retired</option>
              </select>
            </div>
            <div><Label>Manufacturer</Label><Input value={form.manufacturer} onChange={e => set('manufacturer', e.target.value)} data-testid="new-mfr" /></div>
            <div><Label>Model</Label><Input value={form.model} onChange={e => set('model', e.target.value)} data-testid="new-model" /></div>
            <div><Label>Serial Number</Label><Input value={form.serial_number} onChange={e => set('serial_number', e.target.value)} data-testid="new-serial" /></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={e => set('sku', e.target.value)} data-testid="new-sku" /></div>
            <div><Label>UPC / Barcode</Label><Input value={form.upc} onChange={e => set('upc', e.target.value)} className="font-mono" data-testid="new-upc" /></div>
            <div><Label>Vendor</Label><Input value={form.vendor} onChange={e => set('vendor', e.target.value)} /></div>
            <div><Label>Purchase Date</Label><Input type="date" value={form.purchase_date} onChange={e => set('purchase_date', e.target.value)} data-testid="new-pdate" /></div>
            <div><Label>Purchase Cost ($)</Label><Input type="number" min="0" step="0.01" value={form.purchase_cost} onChange={e => set('purchase_cost', e.target.value)} data-testid="new-cost" /></div>
            <div><Label>Warranty Expires</Label><Input type="date" value={form.warranty_expires_at} onChange={e => set('warranty_expires_at', e.target.value)} /></div>
            <div><Label>PO #</Label><Input value={form.purchase_order} onChange={e => set('purchase_order', e.target.value)} /></div>
            <div className="md:col-span-2">
              <Label>Warranty Terms</Label>
              <Input value={form.warranty_terms} onChange={e => set('warranty_terms', e.target.value)} placeholder="e.g. 1-year limited" data-testid="new-warranty-terms" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)}
                        className="w-full mt-1 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#E2E8F0]">
            <Button type="button" variant="outline" onClick={() => navigate('/assets/list')}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#0E7490] hover:bg-[#0E7490]/90 text-white" data-testid="new-submit">
              {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Create
            </Button>
          </div>
        </Card>
      </form>
    </div>
  );
}
